/**
 * TIP Shell — the TRIPWIRE (Phase-1 ENFORCEMENT, not logging).
 *
 * Self-navigation is the one unclosable real-DOM egress channel (SCAFFOLD §1, KC-2):
 * no sandbox flag stops a frame navigating ITSELF, and `navigate-to 'none'` is
 * unreliable in Chrome. The spike PROVED the detector: a second `load` event on the
 * sealed iframe means the frame document was replaced — a self-nav.
 *
 * Where the spike merely refused to re-send identity, Phase-1 ENFORCES:
 *   1. surface a BREACH state   → the UI shows the quarantine immediately
 *   2. POST /admin/kill (source=egress_tripwire) → the server quarantines the app
 *      tenant-wide AND writes the hash-chained `egress_tripwire` audit event
 *      ATOMICALLY. One call does both, so the breach record can't be dropped by a
 *      failed second round-trip.
 *
 * One-shot per frame (fast): the first trip wins; the breach is surfaced BEFORE the
 * kill call resolves so nothing waits on the wire.
 */
import type { BridgeApi } from './bridge.js';

export interface TripwireOptions {
  appId: string;
  api: BridgeApi;
  /** killed=false when first surfaced; flips true once /admin/kill is confirmed. */
  onBreach(reason: string, killed: boolean): void;
  log(kind: 'warn' | 'breach' | 'info', msg: string): void;
}

export class Tripwire {
  private tripped = false;

  constructor(private readonly o: TripwireOptions) {}

  isTripped(): boolean {
    return this.tripped;
  }

  /** Fire once. Surfaces breach synchronously, then enforces (kill + ledger) async. */
  trip(reason: string): void {
    if (this.tripped) return; // one-shot per frame
    this.tripped = true;

    const detectedAt = new Date().toISOString();
    this.o.log('breach', `TRIPWIRE FIRED (${reason}) — quarantining ${this.o.appId} + emitting egress_tripwire`);
    // Surface the breach immediately; do not block the UI on the network.
    this.o.onBreach(reason, false);

    // Enforcement + audit in ONE authoritative call: the server quarantines the app
    // tenant-wide AND writes the hash-chained egress_tripwire event atomically.
    void this.o.api
      .adminKill(this.o.appId, reason, 'egress_tripwire', detectedAt)
      .then((ok) => {
        this.o.log(
          ok ? 'breach' : 'warn',
          ok
            ? `/admin/kill ok — ${this.o.appId} quarantined tenant-wide + egress_tripwire audited`
            : `/admin/kill FAILED — app still flagged BREACHED in UI (fail-loud)`,
        );
        this.o.onBreach(reason, ok);
      })
      .catch((err: unknown) => {
        this.o.log('warn', `/admin/kill error: ${String(err)}`);
      });
  }
}
